<template>
  <div class="card flex justify-center">
    <button
      v-if="!selectedOptions.length"
      type="button"
      @click="toggle"
      :disabled="loading"
      aria-haspopup="true"
      aria-controls="overlay_menu"
      class="rounded-lg py-2 px-3 flex items-center space-x-1 border text-sm border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100 duration-300 h-10 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <figure class="h-5 w-5">
        <img
          src="@/assets/svg/filter-add.svg"
          alt="export"
          class="h-full w-full object-cover"
        />
      </figure>
      <span>{{ title ?? "Filter" }}</span>

      <i v-if="loading" class="pi pi-spinner text-sm animate-spin"></i>
    </button>

    <div
      v-else
      class="rounded-lg py-2 px-3 flex items-center space-x-1 border text-sm border-gray-300 bg-gray-50/20 text-gray-700 hover:bg-gray-50 duration-300 h-10"
    >
      <button
        v-for="option in selectedOptions"
        :key="option.value"
        class="rounded-full px-3 py-1"
        :class="{
          'bg-transparent': selectedOptions.length === 1,
          'bg-gray-100': selectedOptions.length > 1,
        }"
        @click="toggle"
      >
        {{ option.label }}
      </button>

      <button
        type="button"
        @click="clear"
        class="size-5 rounded-full duration-300 hover:bg-gray-100"
      >
        <img
          src="@/assets/svg/close-circle--dark.svg"
          alt="Clear"
          class="h-full w-full object-cover"
        />
      </button>
    </div>

    <Popover v-if="localOptions.length > 0" ref="op">
      <div class="space-y-3 flex flex-col w-[250px] max-h-[350px]">
        <div class="w-full flex-1 overflow-y-auto space-y-2">
          <InputSystem
            :label="''"
            :passwordRevealToggle="false"
            :clearSearch="false"
            :placeholder="'Search'"
            :status="'dropdownSearch'"
            :type="'text'"
            :forgotPassword="false"
            :validation="false"
            v-model="search"
            ><img src="@/assets/svg/search.svg" class="w-5 h-5"
          /></InputSystem>
          <button
            v-for="option in filteredItems"
            :key="option.value"
            class="w-full rounded-lg p-3 flex items-center space-x-2 text-sm text-gray-600 hover:bg-gray-50 duration-300 font-medium"
            @click="toggleOption(option)"
          >
            <Checkbox
              :rounded="true"
              color="#545454"
              v-model="option.selected"
              :id="option.value"
              @update:modelValue="() => toggleOption(option)"
            />
            <p class="text-sm text-left flex-1 font-normal line-clamp-1">
              {{ option.label }}
            </p>
          </button>
        </div>

        <Button theme="blackSolid" @click="handleApply"> Apply </Button>
      </div>
    </Popover>
  </div>
</template>

<script>
import Popover from "primevue/popover";
import Button from "@/components/UI/Buttons/Button.vue";
import Checkbox from "@/components/UI/Checkbox/Checkbox.vue";

export default {
  name: "TableFilter",
  components: {
    Popover,
    Button,
    Checkbox,
  },
  props: {
    title: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedOptions: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    filteredItems() {
      return this.localOptions.filter((item) => {
        const searchLower = this.search?.toLowerCase();
        return item.label.toLowerCase().includes(searchLower);
      });
    },
  },
  data() {
    return {
      localOptions: [],
      items: [
        {
          label: "Export",
          items: [
            {
              label: "Export as CSV",
              icon: "pi pi-file",
              command: () => this.exportAsCSV(),
            },
            {
              label: "Export as PDF",
              icon: "pi pi-file-pdf",
              command: () => this.exportAsPDF(),
            },
          ],
        },
      ],
      search: "",
    };
  },
  methods: {
    toggleOption(option) {
      if (this.multiple) {
        option.selected = !option.selected;
      } else {
        this.localOptions.forEach((o) => (o.selected = o === option));
      }
    },
    toggle(event) {
      this.$refs.op.toggle(event);
      this.syncLocalOptions();
    },
    handleApply() {
      const updatedSelection = this.localOptions
        .filter((o) => o.selected)
        .map(({ value, label }) => ({ value, label }));
      this.$emit("update:modelValue", updatedSelection);
      this.toggle();
    },
    syncLocalOptions() {
      this.localOptions = this.options.map((option) => ({
        ...option,
        selected: this.modelValue.some((v) => v.value === option.value),
      }));
    },
    clear() {
      this.$emit("update:modelValue", []);
    },
  },
  watch: {
    options: {
      immediate: true,
      handler() {
        this.syncLocalOptions();
      },
    },
    modelValue: {
      deep: true,
      handler() {
        this.syncLocalOptions();
      },
    },
  },
};
</script>
